import { createRouter, createWebHistory } from "vue-router"
import AuthLayout from "@/layout/AuthLayout"
import Layout from "@/layout/Layout"
import { employeesRouter } from "./employees"
import { jobsRouter } from "./jobs"
import { toolboxRouter } from "./toolbox"
import { tradePartnersRouter } from "./trade-partners"
import { trainingsRouter } from "./trainings"
import { vendorsRouter } from "./vendors"
import { adminRouter } from "./admin"
import { myJobsRouter } from "./my-jobs"
import store from "@/store/store.js"
import { clearLocalStorageKeepingUtmParams } from "@/utils/utmParams"

const routes = [
  {
    // The site root shows login (GFB-1436). The redirect keeps the query
    // string, and logged-in users are sent on to their dashboard by the auth
    // route check in beforeEach.
    path: "/",
    name: "home",
    redirect: "/login",
  },
  // TODO: Remove this Icons view before launch
  {
    path: "/icons",
    component: () => import(/* webpackChunkName: "demo" */ "@/views/Icons.vue"),
  },
  // TODO: Remove this TradeTypeahead demo route once the component (GFB-1377/
  // 1378/1379) is wired into a real onboarding flow
  {
    path: "/trade-typeahead-demo",
    component: () =>
      import(/* webpackChunkName: "demo" */ "@/views/TradeTypeaheadDemo.vue"),
  },
  {
    path: "/logout",
    name: "logout",
    meta: {
      authRequired: false,
    },
    component: () =>
      import(/* webpackChunkName: "logout" */ "@/views/Logout.vue"),
  },
  {
    path: "/",
    redirect: "dashboard",
    component: Layout,
    meta: {
      authRequired: true,
    },
    children: [
      {
        path: "/dashboard",
        name: "dashboard",
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () =>
          import(/* webpackChunkName: "demo" */ "@/views/Dashboard.vue"),
      },
      {
        path: "/profile",
        name: "profile",
        component: () =>
          import(/* webpackChunkName: "profile" */ "@/views/Profile.vue"),
      },
      {
        path: "/profile/:profileId/:type?",
        name: "profile-id",
        component: () =>
          import(/* webpackChunkName: "profile" */ "@/views/Profile.vue"),
      },
      {
        path: "/messages",
        name: "messages",
        component: () =>
          import(/* webpackChunkName: "messaging" */ "@/views/Messages.vue"),
      },
      {
        path: "/messages/:conversationId",
        name: "message-thread",
        component: () =>
          import(
            /* webpackChunkName: "messaging" */ "@/views/MessageThread.vue"
          ),
      },
      ...employeesRouter,
      ...jobsRouter,
      ...toolboxRouter,
      ...tradePartnersRouter,
      ...trainingsRouter,
      ...vendorsRouter,
      ...myJobsRouter,
      {
        path: "/interview/:profileId/results",
        name: "interview-results",
        component: () =>
          import(
            /* webpackChunkName: "interview" */ "@/views/Interview/InterviewResults.vue"
          ),
      },
      {
        path: "/interview/:profileId",
        name: "interview",
        component: () =>
          import(
            /* webpackChunkName: "interview" */ "@/views/Interview/InterviewForm.vue"
          ),
      },
    ],
  },
  {
    path: "/",
    name: "auth",
    redirect: "login",
    component: AuthLayout,
    meta: {
      authRequired: false,
    },
    children: [
      {
        path: "/login",
        name: "login",
        component: () =>
          import(/* webpackChunkName: "Login" */ "@/views/Login.vue"),
      },
      {
        path: "/signup",
        name: "signup",
        component: () =>
          import(/* webpackChunkName: "Signup" */ "@/views/Register.vue"),
      },
      {
        path: "/confirm-email",
        name: "confirm-email",
        component: () =>
          import(
            /* webpackChunkName: "confirmEmail" */ "@/views/ConfirmEmail.vue"
          ),
      },
      {
        path: "/google-verify",
        name: "google-verify",
        component: () =>
          import(
            /* webpackChunkName: "GoogleVerifyCode" */ "@/views/GoogleVerifyCode.vue"
          ),
      },
      {
        path: "/complete-profile",
        name: "complete-profile",
        component: () =>
          import(
            /* webpackChunkName: "completeProfile" */ "@/views/CompleteProfile.vue"
          ),
      },
      {
        path: "/reset-password",
        name: "resetpassword",
        component: () =>
          import(
            /* webpackChunkName: "ResetPassword" */ "@/views/ResetPassword.vue"
          ),
      },
      {
        path: "/claim-account",
        name: "claim-account",
        component: () =>
          import(
            /* webpackChunkName: "ClaimAccount" */ "@/views/ClaimAccount.vue"
          ),
      },
    ],
  },
  ...adminRouter,
  {
    path: "/find-work",
    name: "find-work",
    component: () =>
      import(/* webpackChunkName: "findWork" */ "@/views/FindWork.vue"),
  },
  {
    // Company counterpart of /find-work (GFB-1413).
    path: "/hire",
    name: "hire",
    component: () =>
      import(/* webpackChunkName: "findWork" */ "@/views/Hire.vue"),
  },
  {
    path: "/privacy-policy",
    component: () =>
      import(/* webpackChunkName: "profile" */ "@/views/PrivacyPolicy.vue"),
  },
  {
    path: "/terms",
    component: () =>
      import(/* webpackChunkName: "profile" */ "@/views/Terms.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    component: () =>
      import(/* webpackChunkName: "[request]" */ "@/views/404.vue"),
  },
]

// propagate metadata recursively to child routes
function propagateMetadata(routes, meta) {
  routes.forEach((route) => {
    if (route.meta === undefined) {
      route.meta = meta
    }
    if (route.children !== undefined) {
      propagateMetadata(route.children, route.meta)
    }
  })
}

propagateMetadata(routes, {})

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  linkExactActiveClass: "active",
  routes: routes,
})

router.beforeEach((to, from, next) => {
  if (to.query.claimToken && to.name !== "claim-account") {
    const { claimToken, ...remainingQuery } = to.query
    const nextPath =
      Object.keys(remainingQuery).length > 0
        ? `${to.path}?${new URLSearchParams(remainingQuery).toString()}`
        : to.path
    return next({
      path: "/claim-account",
      query: { token: claimToken, next: nextPath },
    })
  }

  const accessToken = localStorage.getItem("GFB_ACCESS_TOKEN")
  const userProfile = store.state.userProfile

  // Prevent logged-out users from accessing dashboard
  if ((!accessToken || !userProfile) && to.name === "dashboard") {
    clearLocalStorageKeepingUtmParams()
    return next("/login")
  }

  //check page is protected or not
  if (to.meta.authRequired === true) {
    //access check
    if (!accessToken || !userProfile) {
      clearLocalStorageKeepingUtmParams()
      next({ path: "/login", query: { redirect: to.fullPath } })
    } else {
      if (to.meta.admin === true) {
        if (store.state.user.role == "support") {
          return next()
        } else {
          router.push("/dashboard")
        }
      } else {
        if (store.state.user.role == "support") {
          return next("admin/dashboard")
        } else {
          next()
        }
      }
    }
  } else {
    // Prevent redirect loop: If coming from complete-profile to login, allow
    if (from.name == "complete-profile" && to.name == "login") {
      return next(from.path)
    }

    //Redirect to dashboard if user logged and try to access auth routes.
    // Needs both, like the dashboard check above: a leftover token with no
    // profile (e.g. an expired session) would otherwise bounce
    // /signup -> /dashboard -> /login (GFB-1413).
    if (
      accessToken &&
      userProfile &&
      to.name !== "complete-profile" &&
      to.matched.some((record) => record.name === "auth")
    ) {
      if (store.state.user.role == "support") {
        return next("admin/dashboard")
      } else {
        next("/dashboard")
      }
    } else {
      return next()
    }
  }
})

export default router
